import { CollectionBadges } from '@playbooks/components/collection/collection-badges';
import { CollectionCardActions } from '@playbooks/components/collection/collection-card-actions';
import { CollectionCardHeader } from '@playbooks/components/collection/collection-card-header';
import { CollectionCardMeta } from '@playbooks/components/collection/collection-card-meta';
import { CardRemoveBtn } from '@playbooks/molecules/buttons';
import { DetailCard, DisplayCard, GridCard, PhotoCard, PillLinkCard, SelectCard } from '@playbooks/molecules/cards';
import { Badge } from '@playbooks/ui/components/badge';

const getCollectionHref = collection =>
	collection.appPath ||
	(collection.owner?.uuid && collection.uuid ? `/${collection.owner.uuid}/collections/${collection.uuid}` : '');

export const CollectionDetailCard = ({ collection, loading, className = '' }) => (
	<DetailCard
		icon='layer-group'
		media={<CollectionCardHeader layout='detail' collection={collection} />}
		title={collection.name}
		subtitle={collection.owner?.name}
		text={collection.tagline}
		updatedAt={collection.updatedAt}
		loading={loading}
		className={className}
	/>
);

export const CollectionDisplayCard = ({ collection, children = null, className = '' }) => {
	const href = getCollectionHref(collection);
	return (
		<DisplayCard
			icon='layer-group'
			media={<CollectionCardHeader layout='display' collection={collection} />}
			badges={<CollectionBadges collection={collection} />}
			title={collection.name}
			subtitle={collection.tagline}
			href={href}
			meta={<CollectionCardMeta collection={collection} />}
			className={className}
			footer={<>{children || <CollectionCardActions layout='display' collection={collection} />}</>}
		/>
	);
};

export const CollectionGridCard = ({ collection, context = 'marketplace', children = null, className = '' }) => {
	const href = getCollectionHref(collection);
	const count = collection.stats?.totalBookmarks ?? collection.bookmarks?.length ?? 0;
	return (
		<GridCard
			icon='layer-group'
			badges={<CollectionBadges collection={collection} variant={context} />}
			media={<CollectionCardHeader layout='grid' collection={collection} />}
			title={collection.name}
			text={collection.description}
			meta={<span className='text-xs text-foreground-subtle'>{count} bookmarks</span>}
			href={href}
			className={className}
			actions={<>{children}</>}
		/>
	);
};

export const CollectionMessageCard = ({ collection, actions, className = '' }) => {
	const onRemove = actions?.onRemove;
	const href = getCollectionHref(collection);
	const onClick = e => {
		e.preventDefault();
		onRemove({ ...collection, attachmentId: collection.id, attachmentType: 'Collection' });
	};
	return (
		<PillLinkCard
			icon='layer-group'
			overlayActions={actions && <CardRemoveBtn onClick={onClick} className='-top-3 -right-5' />}
			media={<CollectionCardHeader layout='pill' collection={collection} />}
			title={collection.name}
			text={collection.tagline}
			href={href}
			className={className}
			actions={
				<>
					<Badge>Collection</Badge>
				</>
			}
		/>
	);
};

export const CollectionPhotoCard = ({ collection, children = null, className = '' }) => {
	const href = getCollectionHref(collection);
	return (
		<PhotoCard
			icon='layer-group'
			media={<CollectionCardHeader layout='photo' collection={collection} />}
			title={collection.name}
			text={collection.tagline}
			href={href}
			className={className}
			footer={<>{children || <CollectionCardActions layout='photo' collection={collection} />}</>}
		/>
	);
};

export const CollectionPillCard = ({ collection, loading, children, className = '' }) => {
	const href = getCollectionHref(collection);
	return (
		<PillLinkCard
			icon='layer-group'
			media={<CollectionCardHeader layout='pill' collection={collection} />}
			title={collection.name}
			text={collection.tagline}
			href={href}
			loading={loading}
			className={className}
			actions={<>{children}</>}
		/>
	);
};

export const CollectionSelectCard = ({
	layout = 'select',
	collection,
	selected = [],
	loading = false,
	onClick,
	children = null,
	className = '',
}) => (
	<SelectCard
		layout={layout === 'select-drag' ? 'draggable' : 'default'}
		icon='layer-group'
		badges={<CollectionBadges collection={collection} />}
		media={<CollectionCardHeader layout='select' collection={collection} />}
		title={collection.name}
		text={collection.tagline}
		selected={selected.map(v => v.id).includes(collection.id)}
		loading={loading}
		onClick={() => onClick(collection)}
		className={className}
		actions={<>{children}</>}
	/>
);
